// JsonArgsField.tsx — a textarea for a JSON array of strings (MCP container
// runtime args / stdio args) that keeps the user's in-progress text as a
// draft and shows an inline validation error while it does not parse.
//
// Before this existed the textarea's value was JSON.stringify(committed), so
// any keystroke that left the text momentarily invalid (typing '[' to start
// an array) was thrown away and the field could not be edited at all. Now the
// draft is the source of truth for what is shown; it commits to the parent
// (onChange) only when it parses, and validity is reported via onValidity so
// the page can refuse to save or discover with a stale value.
import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { formatArgs, parseArgsDraft, sameArgs } from './jsonArgs';

interface JsonArgsFieldProps {
  /** Accessible label; also used in console diagnostics. */
  label: string;
  /** The committed value from the parent's state. */
  value: readonly string[] | undefined;
  /** Called with the parsed array whenever the draft becomes valid. */
  onChange: (args: string[]) => void;
  /** Called with false while the draft is invalid, true otherwise (and on unmount). */
  onValidity?: (valid: boolean) => void;
  rows?: number;
}

const JsonArgsField: React.FC<JsonArgsFieldProps> = ({ label, value, onChange, onValidity, rows = 2 }) => {
  const [draft, setDraft] = useState(() => formatArgs(value));
  const [error, setError] = useState('');
  const onValidityRef = useRef(onValidity);
  onValidityRef.current = onValidity;

  // If the parent replaces the committed value (e.g. a reset) while our draft
  // is valid but says something else, adopt the parent's value. An invalid
  // draft is never overwritten: that is the user mid-edit.
  useEffect(() => {
    const parsed = parseArgsDraft(draft);
    if (parsed.ok && !sameArgs(parsed.value, value ?? [])) {
      console.debug(`JsonArgsField[${label}]: parent value changed, resetting draft`);
      setDraft(formatArgs(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // An unmounted field (type switched, modal closed) cannot block saving.
  useEffect(() => () => onValidityRef.current?.(true), []);

  const handleChange = (text: string) => {
    setDraft(text);
    const parsed = parseArgsDraft(text);
    if (parsed.ok) {
      setError('');
      onValidity?.(true);
      onChange(parsed.value);
    } else {
      console.debug(`JsonArgsField[${label}]: draft invalid: ${parsed.error}`);
      setError(parsed.error);
      onValidity?.(false);
    }
  };

  return (
    <>
      <Form.Control
        as="textarea"
        rows={rows}
        aria-label={label}
        className="bg-transparent text-light border-secondary"
        value={draft}
        isInvalid={error !== ''}
        onChange={e => handleChange(e.target.value)}
      />
      <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
    </>
  );
};

export default JsonArgsField;
